import { useEffect, useRef } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { log } from '@/logger/logger.ts';
import { FlexCenVerSpaceBetween, FlexCol, FlexCenVer } from '@/UI/styles';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { FigmaLogo } from '@/UI/Icons/FigmaLogo';
import { color } from '@/colors';
import { getAxiosInstance } from '@/services/axiosConfig';
import { initiateFigmaConnectRedirect } from '@/services/auth/firebase/oidc-providers/figma-oidc-provider';
import { useGlobalStore } from '@/state/global.state';

const logger = log.getLogger('LinkedAccounts');

const FIGMA_CONNECTION_QUERY_KEY = 'figmaConnection';

type FigmaConnection = { connected: true; figmaUserId: string; figmaEmail?: string } | { connected: false };

export function LinkedAccounts() {
  const { updateSnackbarData } = useGlobalStore();
  const queryClient = useQueryClient();

  const { data: connection, isLoading } = useQuery({
    queryKey: [FIGMA_CONNECTION_QUERY_KEY],
    queryFn: async (): Promise<FigmaConnection> => {
      const { data } = await getAxiosInstance().get<FigmaConnection>('/v1/auth/figma/connections');
      return data;
    },
  });

  const connectMutation = useMutation({
    mutationFn: async ({ code, redirectUri }: { code: string; redirectUri: string }) => {
      const { data } = await getAxiosInstance().post<FigmaConnection>('/v1/auth/figma/connect', {
        code,
        redirectUri,
      });
      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData([FIGMA_CONNECTION_QUERY_KEY], data);
      updateSnackbarData({ text: 'Figma account linked', isOpen: true });
    },
    onError: (error) => {
      logger.error('Failed to connect Figma:', error);
      updateSnackbarData({ text: 'Failed to link Figma account', isOpen: true });
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: async () => {
      await getAxiosInstance().delete('/v1/auth/figma/connections');
    },
    onSuccess: () => {
      queryClient.setQueryData([FIGMA_CONNECTION_QUERY_KEY], { connected: false });
      updateSnackbarData({ text: 'Figma account unlinked', isOpen: true });
    },
    onError: (error) => {
      logger.error('Failed to disconnect Figma:', error);
      updateSnackbarData({ text: 'Failed to unlink Figma account', isOpen: true });
    },
  });

  // Handle OAuth callback: detect ?code=&state= in URL params
  const didHandleCallback = useRef(false);
  useEffect(() => {
    if (didHandleCallback.current) return;

    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');

    if (!code || !state) return;
    didHandleCallback.current = true;

    const savedState = localStorage.getItem('weavy_figma_connect_state');
    localStorage.removeItem('weavy_figma_connect_state');

    if (!savedState || savedState !== state) {
      window.history.replaceState({}, '', window.location.pathname + '?section=profile');
      return;
    }

    const redirectUri = localStorage.getItem('weavy_figma_connect_redirect_uri') || '';
    localStorage.removeItem('weavy_figma_connect_redirect_uri');

    // Clear query params from URL
    window.history.replaceState({}, '', window.location.pathname + '?section=profile');

    connectMutation.mutate({ code, redirectUri });
  }, [connectMutation]);

  const handleConnect = () => {
    void initiateFigmaConnectRedirect();
  };

  const isProcessing = connectMutation.isPending || disconnectMutation.isPending;

  if (isLoading) {
    return (
      <FlexCol sx={{ gap: 2, mt: 4 }}>
        <Typography variant="body-lg-md">Linked accounts</Typography>
        <CircularProgress size={20} />
      </FlexCol>
    );
  }

  return (
    <FlexCol sx={{ gap: 2, mt: 4 }}>
      <FlexCol sx={{ gap: 0.5 }}>
        <Typography variant="body-lg-md">Linked accounts</Typography>
        <Typography variant="body-sm-rg" sx={{ color: color.White64_T }}>
          Connect to Figma to access files in Weavy and publish to Community
        </Typography>
      </FlexCol>

      <Box
        sx={{
          display: 'flex',
          border: `1px solid ${color.White08_T}`,
          borderRadius: '8px',
          p: 2,
        }}
      >
        <FlexCenVerSpaceBetween sx={{ width: '100%' }}>
          <FlexCenVer sx={{ gap: 1.5 }}>
            <FigmaLogo />
            <FlexCol>
              <Typography variant="body-std-md">Figma</Typography>
              {connection?.connected && (
                <Typography variant="body-sm-rg" sx={{ color: color.White64_T }}>
                  {connection.figmaEmail || 'Connected'}
                </Typography>
              )}
            </FlexCol>
          </FlexCenVer>

          {connection?.connected ? (
            <ButtonContained
              mode="outlined"
              size="small"
              onClick={() => disconnectMutation.mutate()}
              disabled={isProcessing}
            >
              {disconnectMutation.isPending ? 'Unlinking...' : 'Unlink account'}
            </ButtonContained>
          ) : (
            <ButtonContained mode="outlined" size="small" onClick={handleConnect} disabled={isProcessing}>
              {connectMutation.isPending ? 'Linking...' : 'Link account'}
            </ButtonContained>
          )}
        </FlexCenVerSpaceBetween>
      </Box>
    </FlexCol>
  );
}
